export type DeepBlock =
  | { kind: "p"; text: string }
  | { kind: "list"; items: string[]; ordered?: boolean }
  | { kind: "table"; head: string[]; rows: string[][] }
  | { kind: "callout"; title: string; text: string };

export interface DeepSection {
  heading: string;
  blocks: DeepBlock[];
}

export interface DeepContent {
  eyebrow?: string;
  title: string;
  intro?: string;
  sections: DeepSection[];
}

function Block({ block }: { block: DeepBlock }) {
  if (block.kind === "p") {
    return <p className="mt-4 text-base leading-relaxed text-foreground/90">{block.text}</p>;
  }

  if (block.kind === "list") {
    const items = block.items.map((item) => (
      <li key={item} className="leading-relaxed text-foreground/90">
        {item}
      </li>
    ));
    return block.ordered ? (
      <ol className="mt-4 list-decimal space-y-2 pl-5 text-base marker:text-accent">{items}</ol>
    ) : (
      <ul className="mt-4 list-disc space-y-2 pl-5 text-base marker:text-accent">{items}</ul>
    );
  }

  if (block.kind === "table") {
    return (
      <div className="mt-5 overflow-x-auto rounded-2xl border border-border">
        <table className="w-full text-left text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              {block.head.map((h) => (
                <th key={h} className="px-4 py-3 font-semibold">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {block.rows.map((row) => (
              <tr key={row.join("|")} className="align-top">
                {row.map((cell, i) => (
                  <td key={i} className="px-4 py-3 text-foreground/90">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="mt-5 rounded-2xl border border-accent/25 bg-accent/5 p-6">
      <p className="text-xs font-semibold uppercase tracking-widest text-accent">{block.title}</p>
      <p className="mt-3 text-base leading-relaxed text-foreground">{block.text}</p>
    </div>
  );
}

export function DeepContentSection({ content }: { content: DeepContent }) {
  return (
    <section className="px-6 py-12 lg:px-8 lg:py-16">
      <div className="mx-auto max-w-3xl text-foreground">
        <header className="space-y-3">
          <p className="text-xs font-semibold uppercase tracking-widest text-accent">
            {content.eyebrow ?? "In-depth guidance"}
          </p>
          <h2 className="font-serif text-3xl text-brand sm:text-4xl">{content.title}</h2>
          {content.intro && (
            <p className="text-base leading-relaxed text-muted-foreground">{content.intro}</p>
          )}
        </header>

        <div className="mt-10 space-y-10">
          {content.sections.map((section) => (
            <div key={section.heading}>
              <h3 className="font-serif text-2xl text-brand">{section.heading}</h3>
              {section.blocks.map((block, i) => (
                <Block key={i} block={block} />
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
